import * as React from 'react';
import useMediaQuery from '@mui/material/useMediaQuery';
import Papa from 'papaparse';
import { makeStyles } from '@mui/styles';
import { styled } from '@mui/material/styles';
import { LoadJson } from '../_fn';
import { useSelector, useDispatch } from 'react-redux';
import { dgpaCalendarSelect, dgpaCalendarUpdate } from '../_stores/slice/dgpaCalendar';
import { ButtonCom, Loading } from './';
import { zhHK } from 'date-fns/locale';  // zhTW zhHK
import { Box, TextField } from '@mui/material';
import AdapterDateFns from '@mui/lab/AdapterDateFns';
import LocalizationProvider from '@mui/lab/LocalizationProvider';
import StaticDateRangePicker from '@mui/lab/StaticDateRangePicker';
import MuiDateRangePickerDay from '@mui/lab/DateRangePickerDay';
import { format } from 'date-fns';


const useStyles = makeStyles((theme) => ({
  root: {
    borderRadius: theme.borderRadius.large,
    boxShadow: theme.palette.shadow.main,
    backgroundColor: theme.palette.background.paper,
    position: 'relative',
    overflow: 'hidden',
    [theme.breakpoints.down('sm')]: {
      height: '90vh',
      flexDirection: 'column',
      border: 'none',
      borderRadius: 0,
      boxShadow: 'none',
    },
  },
  buttons: {
    padding: 15,
    width: '100%',
    borderTop: `1px solid ${theme.palette.line.secondary}`,
    display: 'flex',
    columnGap: 20,
    '&>div:nth-child(1)': {
      flex: 1,
    },
    '&>div:not(:first-child)': {
      width: 80,
    },
  },
}));


const DATE_SIZE = {
  desktop: 31,
  mobile: 'calc(100% / 7)'  // 35
};
const dateRangeStyles = makeStyles((theme) => ({
  root: {
    width: 530,
    '& > div': {
      backgroundColor: theme.palette.background.paper,
    },
    '& > div > div, & > div > div > div, & .MuiCalendarPicker-root': {
    },
    '& > div > div > div > div': {
      width: '50%',
      border: 'none !important',
      '&:not(:first-of-type) > div:not(:first-of-type)': {
        borderLeft: `1px solid ${theme.palette.line.main}`,
      },
      '& > div:nth-of-type(1)': {
        // 月 年：排
      },
      '& > div:nth-of-type(2)': {
        // 星期：排
      },
    },
    '& .MuiIconButton-root': {
      // 月 年 左右 button
      color: theme.palette.icon.main,
    },
    '& .MuiTypography-subtitle1': {
      // 月 年
      fontSize: 14,
    },
    '& .MuiTypography-caption': {
      // 星期 title
      margin: 0,
      width: DATE_SIZE.desktop,
      color: theme.palette.text.note,
      fontSize: 12,
      [theme.breakpoints.down('md')]: {
        width: DATE_SIZE.mobile,
      },
    },
    '& .PrivatePickersSlideTransition-root': {
      // 日：區塊
      minWidth: 'unset',
      minHeight: 230, // 6週
      [theme.breakpoints.down('sm')]: {
        minHeight: '50vh',
      },
    },
    '& .PrivatePickersSlideTransition-root [role="row"]': {
      // 日：排
      margin: 0,
      padding: '1px 0',
    },
    '& .MuiPickersDay-dayWithMargin': {
      margin: 0,
    },
    '& .MuiDateRangePickerDay-root': {
      // 日 最外層
      margin: 0,
      width: DATE_SIZE.desktop,
      height: 'auto',
      aspectRatio: 1,
      [theme.breakpoints.down('md')]: {
        width: DATE_SIZE.mobile,
        height: 'auto',
      },
      '& > div': {
        width: '100%',
        height: '100%',
        '&:hover': {
          borderWidth: '1px',
        }
      },
    },
    '& .MuiPickersDay-root': {
      // 日 button
      width: '100%',
      height: '100%',
      fontSize: 12,
      transform: 'none',
      '-webkit-transform': 'none',
      '-moz-transform': 'none',
      '-ms-transform': 'none',
    },
    '& .MuiPickersDay-today': {
      // 今天
      color: theme.palette.primary.text,
      backgroundColor: theme.palette.background.calendar.today,
      border: 'none !important',
    },
    '& .MuiButtonBase-root.MuiPickersDay-root.MuiDateRangePickerDay-day.MuiDateRangePickerDay-notSelectedDate.MuiDateRangePickerDay-dayOutsideRangeInterval': {
      // 日 hover focus
      transition: '.3s',
      border: 'none',
      '&:hover, &:focus': {
        backgroundColor: theme.palette.background.secondary,
      },
    },
    [theme.breakpoints.down('md')]: {
      width: 265,
      '& > div': {
        width: 'inherit',
        minWidth: 'unset',
      },
      '& > div > div': {
        width: 'inherit',
        '& > div:nth-of-type(1)': {
          margin: 0,
          padding: 0,
          height: 50,
          minHeight: 'unset',
          maxHeight: 'unset',
          display: 'block',
          position: 'relative',
          '& > div:nth-of-type(1)': {
            width: 'inherit',
            height: 'inherit',
            maxHeight: 'unset',
            justifyContent: 'center',
          },
          '& > div:nth-of-type(2)': {
            width: '100%',
            height: 'inherit',
            maxHeight: 'unset',
            position: 'absolute',
            top: 0,
            left: 0,
            zIndex: 10,
            '& > div': {
              flex: '1 1 auto',
              border: 'none !important',
            },
            '& > button': {
              width: 50,
            },
          },
        },
      },
      '& > div > div > div.PrivatePickersSlideTransition-root': {
        '& > div': {
          width: '100%',
        },
      },
    },
    [theme.breakpoints.down('sm')]: {
      padding: 20,
      width: '100%',
    },
  }
}));


const DateRangePickerDay = styled(MuiDateRangePickerDay)(
  ({ theme, isHighlighting, isStartOfHighlighting, isEndOfHighlighting }) => ({
    ...(isHighlighting && {
      borderRadius: 0,
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.primary.text,
      '&:hover, &:focus': {
        backgroundColor: theme.palette.primary.dark,
      },
    }),
    ...(isStartOfHighlighting && {
      borderTopLeftRadius: '50%',
      borderBottomLeftRadius: '50%',
    }),
    ...(isEndOfHighlighting && {
      borderTopRightRadius: '50%',
      borderBottomRightRadius: '50%',
    }),
  }),
);

const DateRange = ({dateValue, setDateValue, disablePast, minDate}) => {

  const classes = dateRangeStyles();
  const md = useMediaQuery((theme) => theme.breakpoints.down('md'), {noSsr: true});
  const dgpaCalendarStore = useSelector(dgpaCalendarSelect);
  const calendarType = md ? 'mobile' : 'desktop';


  const renderWeekPickerDay = (date, dateRangePickerDayProps) => {

    let _year = format(dateRangePickerDayProps.day, 'yyyy') 

    if(dgpaCalendarStore[_year]) {

      let _year = format(dateRangePickerDayProps.day, 'yyyy') 
      let _date = format(dateRangePickerDayProps.day, 'yyyy/MM/dd');
      let _dateInfo = dgpaCalendarStore[_year].find(item => item.date === _date);

      if(_dateInfo.holiday && _dateInfo.short !== '') {
        return (
          <DateRangePickerDay
            {...dateRangePickerDayProps}
          >
            <Box
              sx={{
                color: theme => theme.palette.text.red,
                fontSize: 13,
                fontWeight: 600,
              }}
            >
              {_dateInfo.short}
            </Box>
          </DateRangePickerDay>
        )
      } else {
        return <DateRangePickerDay {...dateRangePickerDayProps} />;
      }

    } else {
      return <DateRangePickerDay {...dateRangePickerDayProps} />;
    }

  };


  return (
    <Box className={classes.root}>

      <LocalizationProvider dateAdapter={AdapterDateFns} locale={zhHK}>
        <StaticDateRangePicker
          disablePast={disablePast}
          showToolbar={false}
          displayStaticWrapperAs={calendarType}
          label='date_range'
          value={dateValue}
          onChange={(newValue) => setDateValue(newValue)}
          renderDay={renderWeekPickerDay}
          renderInput={(startProps, endProps) => (
            <>
              <TextField {...startProps} name='CalendarStartProps' />
              <Box sx={{ mx: 2 }}> to </Box>
              <TextField {...endProps} name='CalendarEndProps' />
            </>
          )}
          disableHighlightToday={true}
          minDate={minDate ? new Date(minDate) : null}
        />
      </LocalizationProvider>

    </Box>
  )
}


/**
 * 日曆
 * @param {String} selectData 日期初值
 * @param {Hooks} setSelectData 設定選擇的日期
 * @param {function} closeClick 關閉日曆
 * @param {Boolean} disablePast 禁選過期的日期
 * @param {string} minDate 最小日期
 * @param {string} maxDate 最大日期
 */
export function Calendar({selectData, setSelectData, closeClick, disablePast, minDate, maxDate}) {

  const classes = useStyles();
  const dispatch = useDispatch();
  const dgpaCalendarStore = useSelector(dgpaCalendarSelect);
  const [dateValue, setDateValue] = React.useState([selectData.from, selectData.to]);

  /**
   * 轉換日行事曆資料格式
   * item[0] 西元日期
   * item[1] 星期
   * item[2] 是否放假
   * item[3] 備註
   */
  const toJson = ({data}) => {
    let newList = [];
    data.forEach((item, index) => {
      if(index > 0) {
        let newItem = {
          date: `${item[0].substring(0,4)}/${item[0].substring(4,6)}/${item[0].substring(6,8)}`,
          month: Number(item[0].substring(4,6)),
          day: Number(item[0].substring(6,8)),
          week: item[1],
          holiday: item[2] === '2' ? true : false,
          note: item[3],
          short: item[4] ? item[4] : ''
        }
        newList.push(newItem);
      }
    });
    return newList
  }

  React.useEffect(() => {
    if(dgpaCalendarStore === null) {
      // 載入行政院行事曆
      LoadJson('/dgpaCalendar/calendar.json')
      .then(function(yearRes) {
        let newCalendar = {};
        yearRes.forEach(item => {
          Papa.parse(`/dgpaCalendar/${item.file}`, {
            download: true,
            complete: function(res) {
              newCalendar[item.year] = toJson({data: res.data});
              let len = Object.keys(newCalendar).length;
              if(len === yearRes.length) {
                dispatch(dgpaCalendarUpdate({...newCalendar}));
              }
            }
          });
        });
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 清除日曆
  const clearClick = () => {
    setSelectData({
      from: null,
      to: null
    });
    closeClick();
  }

  // 送出日曆
  const enterClick = () => {
    console.log('dateValue ==>', dateValue);
    let _from = (dateValue[0] === null || dateValue[0] === '') ? '' : format(new Date(dateValue[0]), 'yyyy/MM/dd');
    let _to = (dateValue[1] === null || dateValue[1] === '') ? _from : format(new Date(dateValue[1]), 'yyyy/MM/dd');
    setSelectData({
      from: _from,
      to: _to,
    });
    closeClick();
  }


  return (
    <Box className={classes.root}>

      {
        dgpaCalendarStore === null
        ?
          <Box
            sx={{
              width: '100%',
              minWidth: '360px',
              maxWidth: '530px',
              minHeight: '320px',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <Loading />
          </Box>
        :
          <DateRange
            dateValue={dateValue}
            setDateValue={setDateValue}
            disablePast={disablePast}
            minDate={minDate}
            maxDate={maxDate}
          />
      }

      <Box className={classes.buttons}>
        <Box></Box>
        <Box>
          <ButtonCom content='清除' onClick={clearClick} color='white' />
        </Box>
        <Box>
          <ButtonCom content='選擇' onClick={enterClick} />
        </Box>
      </Box>

    </Box>
  )
}